import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import usePageMeta from "@/hooks/usePageMeta";
import { films, specialList } from "@/lib/films";

const principles = [
  {
    title: "先核对，再下笔",
    text: "每一条影视条目的导演、主演、年份与地区都会逐项核对，宁可少写，不写错。",
  },
  {
    title: "剧情写完整，但不猎奇",
    text: "剧情介绍覆盖前情设定与关键转折，同时避免以剧透为卖点。",
  },
  {
    title: "影评保留分歧",
    text: "同一部作品可以有两篇观点相反的影评，我们不要求作者统一口径。",
  },
  {
    title: "不提供播放与下载",
    text: "本站不聚合资源、不提供任何播放链接，只做内容层面的介绍与讨论。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "内容合作", value: "contact@renshou-film.com" },
  { icon: "ri-mail-send-line", label: "影评投稿", value: "review@renshou-film.com" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "人兽影视网（演示）" },
  { icon: "ri-map-pin-line", label: "编辑部地址", value: "北京市朝阳区光影路 12 号 5 层" },
];

export default function About() {
  const reviewCount = films.reduce((total, film) => total + film.reviews.length, 0);

  usePageMeta({
    title: "关于我们 - 人兽影视网",
    description:
      "人兽影视网是一个专注电影与电视剧内容的影视门户，提供剧情介绍、演员资料与影评分析。了解我们的编辑原则、联系方式与版权声明。",
    keywords: "关于人兽影视网,影视门户,编辑原则,联系方式,版权声明,人兽",
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <section className="container-portal pt-6 md:pt-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-900">关于我们</span>
          </nav>

          <div className="mt-5 grid grid-cols-1 items-center gap-8 border-b border-background-200 pb-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-12">
            <div>
              <span className="label-caps text-[11px] text-accent-700">About Renshou Film</span>
              <h1 className="mt-3 text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-[42px]">
                关于人兽影视网
              </h1>
              <p className="mt-5 text-[15px] leading-8 text-foreground-700">
                人兽影视网是一个以内容为核心的影视门户。名字取自「人与故事」的直白表达，我们希望它足够好记，也足够诚恳。
              </p>
              <p className="mt-3 text-[15px] leading-8 text-foreground-600">
                我们把每天新增的影视信息整理成可检索、可比较的条目：剧情梗概、演员表、上映信息与影评观点都放在同一页里。不追逐热搜，也不为流量改写结论。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category/movie"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-movie-2-line" />
                  浏览电影
                </Link>
                <Link
                  to="/special/silent-current"
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-article-line" />
                  查看影评专题
                </Link>
              </div>
            </div>

            <div className="relative h-[240px] overflow-hidden rounded-lg md:h-[300px]">
              <img
                src="https://readdy.ai/api/search-image?query=quiet%20editorial%20workspace%20with%20a%20vintage%20film%20projector%20film%20reels%20on%20a%20wooden%20desk%20and%20warm%20afternoon%20light%20through%20a%20window%20deep%20navy%20shadows%20with%20amber%20highlights%20cinematic%20still%20life%20no%20text%20high%20detail&width=1200&height=800&seq=rs-about-editorial&orientation=landscape"
                alt="人兽影视网编辑部工作场景"
                title="人兽影视网 关于我们"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/60 to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-md bg-background-50/95 px-3 py-2">
                <i className="ri-quill-pen-line text-accent-600" />
                <span className="text-[11px] font-medium text-foreground-800">内容由编辑部独立整理</span>
              </div>
            </div>
          </div>
        </section>

        <section className="container-portal mt-10">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 md:grid-cols-4">
            {[
              { value: films.length, label: "影视条目" },
              { value: reviewCount, label: "影评文章" },
              { value: specialList.length, label: "专题策划" },
              { value: 5, label: "编辑成员" },
            ].map((stat) => (
              <div key={stat.label} className="bg-background-50 px-4 py-5 text-center md:px-6">
                <p className="font-label text-3xl leading-none text-primary-700">{stat.value}</p>
                <p className="mt-2 text-[11px] text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="container-portal mt-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="h-[3px] w-6 rounded-full bg-accent-500" />
              <span className="label-caps text-[11px] text-accent-700">Principles</span>
            </div>
            <h2 className="mt-3 text-xl font-bold tracking-tight text-foreground-950 md:text-[26px]">
              我们的四条编辑原则
            </h2>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {principles.map((item, index) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-3">
                  <span className="font-label text-lg text-accent-500">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-950">{item.title}</h3>
                </div>
                <p className="mt-2.5 text-[13px] leading-6 text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="container-portal mt-12">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-10">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-[3px] w-6 rounded-full bg-accent-500" />
                <span className="label-caps text-[11px] text-accent-700">Contact</span>
              </div>
              <h2 className="mt-3 text-xl font-bold tracking-tight text-foreground-950 md:text-[26px]">
                联系方式
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                内容纠错、合作洽谈或影评投稿，都可以通过以下方式联系我们。工作日内通常在 48 小时内回复。
              </p>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {contacts.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] text-foreground-500">{item.label}</span>
                      <span className="mt-0.5 block break-all text-[13px] text-foreground-900">
                        {item.value}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] leading-5 text-foreground-500">
                以上邮箱与社交账号为演示信息，仅用于展示页面结构，暂未开通真实通信渠道。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="text-base font-bold text-foreground-950">使用说明与版权声明</h2>
              <div className="mt-4 space-y-4 text-[13px] leading-7 text-foreground-700">
                <p>
                  本站所有影视条目、剧情简介与影评均为示例内容，用于展示门户型网站的信息组织方式，不代表任何真实作品的官方资料。
                </p>
                <p>
                  站内文字内容版权归人兽影视网所有，转载请注明来源并保留原文链接。页面中使用的示意图片由生成方式制作，仅作视觉展示。
                </p>
                <p>
                  本站不提供播放、下载、票务或任何付费服务，也不存储任何影视资源文件。如权利人认为页面内容存在不当使用，可通过上方邮箱与我们联系。
                </p>
              </div>
              <div className="mt-5 border-t border-background-300/60 pt-4">
                <p className="label-caps text-[10px] text-foreground-500">友情链接</p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs text-foreground-600">
                  <span>光影资料馆</span>
                  <span>剧集观察站</span>
                  <span>影展手册</span>
                  <span>城市放映室</span>
                </div>
              </div>
              <Link
                to="/"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 transition-colors hover:text-accent-600 cursor-pointer"
              >
                返回首页
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}